import "../../common/elements/Header.scss";
import { useContext, useEffect, useReducer } from "react";
import translate from "@/i18n/translate";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { MyTasks } from "./MyTasks";
import OrganizationProgramInsights from "./OrganizationProgramInsights";
import { RecentCourseEvaluation } from "./RecentCourseEvaluation";
import {
  defacultFsDeeplinks,
  FsDeeplinks,
  ReportingYear,
  notifications
} from "@/features/common/interfaces/Interface";
import {
  checkIfCesProductIntegrated,
  checkIfFsProductIntegrated,
  getCurrentReportingYear
} from "@/features/common/helper";
import { sendRequest } from "@/common/http";
import { WmButton, WmSnackbar } from "@watermarkinsights/ripple-react";
import { ReviewsInProgress } from "./ReviewsInProgress";
import { ResponseRateOverview } from "./ResponseRateOverview";
import { useLocation, useNavigate } from "react-router-dom";
import { WmSnackbarCustomEvent } from "@watermarkinsights/ripple";
import { useIntl } from "react-intl";
import { usePageTitle } from '@/hooks/usePageTitle';

interface HomeComponentState {
  currentReportingYear: ReportingYear | null;
  fsDeeplinks: FsDeeplinks;
}
interface Action {
  type: string;
  currentReportingYear: ReportingYear | null;
  fsDeeplinks: FsDeeplinks;
}

const defaultAction: Action = {
  type: "",
  currentReportingYear: null,
  fsDeeplinks: defacultFsDeeplinks
};

const initialState: HomeComponentState = {
  currentReportingYear: null,
  fsDeeplinks: defacultFsDeeplinks
};

const stateReducer = (state: HomeComponentState, action: Action) => {
  switch (action.type) {
    case "setCurrentReportingYear":
      return {
        ...state,
        currentReportingYear: action.currentReportingYear
      };
    case "setFsDeeplinks":
      return {
        ...state,
        fsDeeplinks: action.fsDeeplinks
      };
    default:
      return state;
  }
};

const navigateTo = (url: string) => {
  window.location.href = url;
};

export default function HomeComponent(): JSX.Element {
  const { sysUser, integratedProducts } =
    useContext<AppContextInterface>(AppContext);
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();

  const snackbarMessage = intl.formatMessage({
    id: "app.home.fsActivitySaved"
  });

  useEffect(() => {
    if (state.fsDeeplinks.activities) {
      const searchParams = new URLSearchParams(location.search);
      const facultyActivityStatus = searchParams.get("status");
      if (facultyActivityStatus === "fs-activity-save-success") {
        setTimeout(() => {
          addSnackBar();
        }, 2000);
        searchParams.delete("status");
        navigate(`${location.pathname}?${searchParams.toString()}`, {
          replace: true
        });
      }
    }
  }, [state.fsDeeplinks]);

  useEffect(() => {
    if (sysUser && sysUser.roles.includes("administrator")) {
      const applyResult = (result: ReportingYear[]) => {
        const currentReportingYear = getCurrentReportingYear(result);
        dispatch({
          ...defaultAction,
          type: "setCurrentReportingYear",
          currentReportingYear: currentReportingYear
        });
      };

      sendRequest({ uri: "/sa/reporting_years", camelize: true }, applyResult);
    }
  }, [sysUser]);

  useEffect(() => {
    if (checkIfFsProductIntegrated(integratedProducts)) {
      const applyResult = (result: FsDeeplinks) => {
        dispatch({
          ...defaultAction,
          type: "setFsDeeplinks",
          fsDeeplinks: result
        });
      };

      sendRequest(
        {
          uri: "/faculty_success/deeplinks",
          camelize: true
        },
        applyResult
      );
    }
  }, [integratedProducts]);

  let notifications: notifications[] = [];

  function addSnackBar() {
    const snackbar: any = document.getElementById("wm-snackbar");
    const randomId = `snack-${Math.floor(Math.random() * 100) + 1}`;
    const newNotification = {
      id: randomId,
      message: snackbarMessage,
      link: ""
    };
    notifications = [newNotification, ...notifications];
    snackbar.notifications = JSON.stringify(notifications);
  }

  function dismissSnackBar(ev: WmSnackbarCustomEvent<Object>) {
    const snackbar: any = document.getElementById(ev.target.id);
    notifications = notifications.filter(
      (snack: notifications) => snack.id !== ev.detail.id
    );
    snackbar.notifications = JSON.stringify(notifications);
  }

  const pageTitle = intl.formatMessage({ id: 'app.pageTitle.home' });
  usePageTitle(pageTitle);

  return (
    <>
      {sysUser && (
        <>
          <div className="home-page-header">
            <div
              className={
                sysUser.roles.includes("administrator")
                  ? "home-title-container"
                  : "home-title-container container-padding"
              }
            >
              <div>
                <div className="home base-font">{translate("app.home")}</div>
                <h2 id="main-heading" className="home-title">
                  {translate("app.home.title", {
                    username: `${sysUser.firstName}`
                  })}
                </h2>
                <div className="home-title-description">
                  {translate("app.home.titleDesc")}
                </div>
              </div>
              {state.fsDeeplinks["activities:add"] && (
                <AddActivity
                  activitiesLink={state.fsDeeplinks["activities:add"]}
                />
              )}
            </div>
          </div>
          <div
            className={
              sysUser.roles.includes("administrator")
                ? "home-page-wrapper main-content-body"
                : "home-page-wrapper container-padding"
            }
          >
            <MyTasks />
            {checkIfFsProductIntegrated(integratedProducts) && (
              <ReviewsInProgress />
            )}
            {checkIfCesProductIntegrated(integratedProducts) && (
              <div className="course-evaluations">
                <ResponseRateOverview />
                <RecentCourseEvaluation />
              </div>
            )}

            <OrganizationProgramInsights
              currentReportingYear={state.currentReportingYear}
            />
          </div>
          <WmSnackbar
            notifications="[]"
            id="wm-snackbar"
            onWmSnackbarSnackFinished={(event) => dismissSnackBar(event)}
          ></WmSnackbar>
        </>
      )}
    </>
  );
}

interface AddActivityProps {
  activitiesLink: string;
}
const AddActivity = ({ activitiesLink }: AddActivityProps): JSX.Element => {
  return (
    <div className="add-activity-button">
      <WmButton buttonType="primary" onClick={() => navigateTo(activitiesLink)}>
        {translate("app.home.fs.activity")}
      </WmButton>
    </div>
  );
};
